import { CONSULTATION_NOTE_MED_LABELS } from "../../../../utils/constants";
import {
    IAddendumDetails,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import NoteContentItem from "../NoteContentItem/NoteContentItem";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import CheckIcon from "../../../../icons/Check";
import { cn, removeEnumUnderscore } from "../../../../utils/helpers";

interface ViewConsultationMedNoteProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewConsultationMedNote({
    noteContent,
    fieldAddendums,
}: ViewConsultationMedNoteProps) {
    const isCheckboxChecked = (checkboxValue: string, key: string) => {
        return Boolean(
            noteContent?.[key]?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };
    return (
        <>
            <NoteContentItem
                headerText={CONSULTATION_NOTE_MED_LABELS.session_location}
                fieldId="session_location"
                addendums={fieldAddendums?.session_location}
                content={
                    noteContent.session_location
                        ? removeEnumUnderscore(noteContent.session_location)
                        : "--"
                }
            />
            {noteContent.session_location === SessionLocations.Telehealth && (
                <>
                    <NoteContentItem
                        headerText={
                            CONSULTATION_NOTE_MED_LABELS.location_of_the_client
                        }
                        fieldId="location_of_the_client"
                        addendums={fieldAddendums?.location_of_the_client}
                        content={
                            noteContent.location_of_the_client
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_client
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_client === "other" && (
                        <NoteContentItem
                            headerText={
                                CONSULTATION_NOTE_MED_LABELS.other_location_of_the_client
                            }
                            fieldId="other_location_of_the_client"
                            addendums={
                                fieldAddendums?.other_location_of_the_client
                            }
                            content={
                                noteContent.other_location_of_the_client || "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            CONSULTATION_NOTE_MED_LABELS.location_of_the_provider
                        }
                        fieldId="location_of_the_provider"
                        addendums={fieldAddendums?.location_of_the_provider}
                        content={
                            noteContent.location_of_the_provider
                                ? removeEnumUnderscore(
                                      noteContent.location_of_the_provider
                                  )
                                : "--"
                        }
                    />
                    {noteContent.location_of_the_provider === "other" && (
                        <NoteContentItem
                            headerText={
                                CONSULTATION_NOTE_MED_LABELS.other_location_of_the_provider
                            }
                            fieldId="other_location_of_the_provider"
                            addendums={
                                fieldAddendums?.other_location_of_the_provider
                            }
                            content={
                                noteContent.other_location_of_the_provider ||
                                "--"
                            }
                        />
                    )}
                    <NoteContentItem
                        headerText={
                            CONSULTATION_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                        }
                        fieldId="consent_was_received_for_the_teletherapy_session"
                        addendums={
                            fieldAddendums?.consent_was_received_for_the_teletherapy_session
                        }
                        content={
                            noteContent.consent_was_received_for_the_teletherapy_session
                                ? "Yes"
                                : "No"
                        }
                    />
                    <NoteContentItem
                        headerText={
                            CONSULTATION_NOTE_MED_LABELS.the_session_was_conducted_via_video
                        }
                        fieldId="the_session_was_conducted_via_video"
                        addendums={
                            fieldAddendums?.the_session_was_conducted_via_video
                        }
                        content={
                            noteContent.the_session_was_conducted_via_video
                                ? "Yes"
                                : "No"
                        }
                    />
                </>
            )}

            <NoteContentItem
                headerText={
                    CONSULTATION_NOTE_MED_LABELS.prolonged_care_different_day_as_em_visit
                }
                fieldId="prolonged_care_different_day_as_em_visit"
                addendums={
                    fieldAddendums?.prolonged_care_different_day_as_em_visit
                }
                content={
                    noteContent?.prolonged_care_different_day_as_em_visit
                        ?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                "Time spent preparing to see patient",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Time spent preparing to see patient
                                </div>
                            )}

                            {isCheckboxChecked(
                                "Obtaining / performing history and exam",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Obtaining / performing history and exam
                                </div>
                            )}

                            {isCheckboxChecked(
                                "Counseling and care coordination",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Counseling and care coordination
                                </div>
                            )}

                            {isCheckboxChecked(
                                "Ordering of medications / tests / procedures",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Ordering of medications / tests / procedures
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Communicating with other health care professionals",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Communicating with other health care
                                    professionals
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Documenting clinical information",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Documenting clinical information
                                </div>
                            )}

                            {isCheckboxChecked(
                                "Review and interpretation of tests",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Review and interpretation of tests
                                </div>
                            )}

                            {isCheckboxChecked(
                                "Communication to patient / family / caregiver",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Communication to patient / family /
                                    caregiver
                                </div>
                            )}

                            {isCheckboxChecked(
                                "Other",
                                "prolonged_care_different_day_as_em_visit"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Other
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    CONSULTATION_NOTE_MED_LABELS.details_of_prolonged_care_different_day_as_em_visit
                }
                fieldId="details_of_prolonged_care_different_day_as_em_visit"
                addendums={
                    fieldAddendums?.details_of_prolonged_care_different_day_as_em_visit
                }
                content={
                    noteContent.details_of_prolonged_care_different_day_as_em_visit ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={CONSULTATION_NOTE_MED_LABELS.modality}
                fieldId="modality"
                addendums={fieldAddendums?.modality}
                content={
                    noteContent.modality
                        ? removeEnumUnderscore(noteContent.modality)
                        : "--"
                }
            />
            <NoteContentItem
                headerText={CONSULTATION_NOTE_MED_LABELS.total_amount_of_time}
                fieldId="total_amount_of_time"
                addendums={fieldAddendums?.total_amount_of_time}
                content={noteContent.total_amount_of_time || "--"}
            />
            <NoteContentItem
                headerText={CONSULTATION_NOTE_MED_LABELS.condition_type}
                fieldId="condition_type"
                addendums={fieldAddendums?.condition_type}
                content={
                    noteContent.condition_type
                        ? removeEnumUnderscore(noteContent.condition_type)
                        : "--"
                }
            />
            <NoteContentItem
                headerText={
                    CONSULTATION_NOTE_MED_LABELS.condition_type_additional_details
                }
                fieldId="condition_type_additional_details"
                addendums={fieldAddendums?.condition_type_additional_details}
                content={noteContent.condition_type_additional_details || "--"}
            />
            <NoteContentItem
                headerText={CONSULTATION_NOTE_MED_LABELS.condition_status}
                fieldId="condition_status"
                addendums={fieldAddendums?.condition_status}
                content={
                    noteContent.condition_status
                        ? removeEnumUnderscore(noteContent.condition_status)
                        : "--"
                }
            />
            <NoteContentItem
                headerText={
                    CONSULTATION_NOTE_MED_LABELS.condition_status_additional_details
                }
                fieldId="condition_status_additional_details"
                addendums={fieldAddendums?.condition_status_additional_details}
                content={
                    noteContent.condition_status_additional_details || "--"
                }
            />
        </>
    );
}
